import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, FileText, Download, TrendingUp, TrendingDown, Check, Minus, Link2, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { formatCurrency, formatPercent, API } from "../lib/helpers";

const MetricRow = ({ label, values, higherIsBetter, isSection }) => {
  if (isSection) {
    return (
      <tr data-testid={`comparison-section-${label}`}>
        <td colSpan={values.length + 1} className="bg-secondary/50 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {label}
        </td>
      </tr>
    );
  }

  // Find the best value index
  let bestIdx = -1;
  if (higherIsBetter !== undefined && values.length > 1) {
    const numeric = values.map((v, i) => {
      const num = parseFloat(String(v).replace(/[$,%/mo]/g, "").replace(/,/g, ""));
      return isNaN(num) ? null : { i, num };
    }).filter(Boolean);
    if (numeric.length > 0) {
      bestIdx = higherIsBetter
        ? numeric.reduce((a, b) => (b.num > a.num ? b : a)).i
        : numeric.reduce((a, b) => (b.num < a.num ? b : a)).i;
    }
  }

  return (
    <tr className="border-b border-border/50 hover:bg-secondary/20 transition-colors">
      <td className="px-3 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">{label}</td>
      {values.map((v, i) => (
        <td key={i} className={`px-3 py-2 text-xs text-center font-mono ${
          i === bestIdx ? "text-emerald-500 font-semibold" : "text-foreground"
        }`}>
          {v === "PASS" ? (
            <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30 text-[10px]">PASS</Badge>
          ) : v === "FAIL" ? (
            <Badge className="bg-red-500/15 text-red-500 border-red-500/30 text-[10px]">FAIL</Badge>
          ) : v}
        </td>
      ))}
    </tr>
  );
};

export default function ComparisonModal({ isOpen, onClose, selectedItems, analysisParams }) {
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  if (!selectedItems || selectedItems.length < 2) return null;

  const getFullItems = async () => {
    return Promise.all(
      selectedItems.map(async (item) => {
        try {
          const resp = await axios.get(`${API}/properties/${item.property.id}/analysis`, { params: analysisParams });
          return { property: item.property, analysis: resp.data };
        } catch {
          return { property: item.property, analysis: item.analysis || {} };
        }
      })
    );
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const fullItems = await getFullItems();
      const response = await axios.post(`${API}/export/comparison-pdf`, {
        properties: fullItems,
      }, { responseType: "blob" });

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RealixData_Comparison_${fullItems.length}_Properties.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Comparison PDF downloaded!");
    } catch {
      toast.error("Failed to export comparison PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const fullItems = await getFullItems();
      const resp = await axios.post(`${API}/comparisons/share`, {
        properties: fullItems,
      });
      const url = `${window.location.origin}/compare/${resp.data.share_id}`;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard!");
    } catch {
      toast.error("Failed to create share link");
    } finally {
      setSharing(false);
    }
  };

  const copyShareUrl = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied!");
    }
  };

  const metrics = [
    { section: "Property Details" },
    { label: "Price", key: (p, a) => formatCurrency(p.price) },
    { label: "Type", key: (p) => (p.property_type || "").replace("_", " ") },
    { label: "Beds / Baths", key: (p) => `${p.bedrooms}bd / ${p.bathrooms}ba` },
    { label: "Sq Ft", key: (p) => `${(p.sqft || 0).toLocaleString()}` },
    { label: "Year Built", key: (p) => p.year_built || "N/A" },
    { section: "Financial Metrics" },
    { label: "Deal Score", key: (p, a) => `${a?.deal_score || 0}/100`, higher: true },
    { label: "BRRRR Score", key: (p, a) => `${a?.brrrr_score || 0}/100`, higher: true },
    { label: "Monthly Cash Flow", key: (p, a) => formatCurrency(a?.cash_flow_monthly || 0), higher: true },
    { label: "Cap Rate", key: (p, a) => formatPercent(a?.cap_rate || 0), higher: true },
    { label: "Cash-on-Cash", key: (p, a) => formatPercent(a?.cash_on_cash_return || 0), higher: true },
    { label: "1% Rule", key: (p, a) => a?.one_percent_rule ? "PASS" : "FAIL" },
    { label: "50% Rule", key: (p, a) => a?.fifty_percent_rule ? "PASS" : "FAIL" },
    { section: "Income & Expenses" },
    { label: "Est. Rent/mo", key: (p, a) => formatCurrency(a?.estimated_monthly_rent || 0), higher: true },
    { label: "Mortgage/mo", key: (p, a) => formatCurrency(a?.monthly_mortgage || 0), higher: false },
    { label: "Total Expenses/mo", key: (p, a) => formatCurrency(a?.total_monthly_expenses || 0), higher: false },
    { label: "NOI/mo", key: (p, a) => formatCurrency(a?.noi_monthly || 0), higher: true },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] p-0 bg-card border-border" data-testid="comparison-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-bold">
                Property Comparison
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Comparing {selectedItems.length} properties side-by-side
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleShare}
                disabled={sharing}
                size="sm"
                variant="outline"
                data-testid="comparison-share-btn"
              >
                {sharing ? <Copy className="w-4 h-4 mr-1.5 animate-pulse" /> : <Link2 className="w-4 h-4 mr-1.5" />}
                {sharing ? "Creating..." : "Share Link"}
              </Button>
              <Button
                onClick={handleExportPDF}
                disabled={exporting}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="comparison-export-pdf-btn"
              >
                {exporting ? <Download className="w-4 h-4 mr-1.5 animate-bounce" /> : <FileText className="w-4 h-4 mr-1.5" />}
                {exporting ? "Exporting..." : "Export PDF"}
              </Button>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh]">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="comparison-table">
              <thead>
                <tr className="bg-secondary">
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground w-36">Metric</th>
                  {selectedItems.map((item, i) => (
                    <th key={i} className="px-3 py-2.5 text-center min-w-[140px]">
                      <div className="text-xs font-semibold text-foreground truncate max-w-[160px]" title={item.property.address}>
                        {item.property.address}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {item.property.city}, {item.property.state}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.map((m, idx) => {
                  if (m.section) {
                    return <MetricRow key={`s-${idx}`} label={m.section} values={[]} isSection />;
                  }
                  const values = selectedItems.map((item) =>
                    m.key(item.property, item.analysis)
                  );
                  return (
                    <MetricRow
                      key={m.label}
                      label={m.label}
                      values={values}
                      higherIsBetter={m.higher}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </ScrollArea>

        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <p className="text-[10px] text-emerald-500">
              Green values indicate the best metric among compared properties
            </p>
            {shareUrl && (
              <button
                onClick={copyShareUrl}
                className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground bg-secondary/50 rounded px-2 py-1 transition-colors"
                data-testid="comparison-share-url"
              >
                <Copy className="w-3 h-3" />
                <span className="max-w-[200px] truncate">{shareUrl}</span>
              </button>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="comparison-close-btn">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
